import { useState, useMemo } from "react";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum, monthlyPayment } from "@/lib/finance";
import { fmtUSD } from "@/lib/format";

// VA Loan: 0% down allowed, no PMI, one-time funding fee financed into loan.
// Default fee = 2.15% (first use, 0% down). Disabled vets exempt.
export default function VaLoanPage() {
    const [homePrice, setHomePrice] = useState(450000);
    const [downPct, setDownPct] = useState(0);
    const [rate, setRate] = useState(6.25);
    const [years, setYears] = useState(30);
    const [fundingFeePct, setFundingFeePct] = useState(2.15);
    const [propertyTax, setPropertyTax] = useState(1.1);
    const [insurance, setInsurance] = useState(1500);
    const [financeFee, setFinanceFee] = useState(true);

    const result = useMemo(() => {
        const hp = toNum(homePrice);
        const dp = toNum(downPct);
        const r = toNum(rate);
        const y = Math.max(1, toNum(years));
        const down = (hp * dp) / 100;
        const baseLoan = Math.max(0, hp - down);
        const fee = (baseLoan * toNum(fundingFeePct)) / 100;
        const loan = financeFee ? baseLoan + fee : baseLoan;
        const pi = monthlyPayment(loan, r, y);
        const tax = (hp * (toNum(propertyTax) / 100)) / 12;
        const ins = toNum(insurance) / 12;
        const monthly = pi + tax + ins;
        return { monthly, pi, tax, ins, down, fee, loan, baseLoan, totalPaid: pi * y * 12 };
    }, [homePrice, downPct, rate, years, fundingFeePct, propertyTax, insurance, financeFee]);

    return (
        <CalculatorShell
            slug="va-loan"
            explainer={
                                <>
                    <h2>How this VA loan calculator works</h2>
                    <p>
                        A VA loan calculator estimates your monthly payment for a VA-backed mortgage, including
                        the funding fee unique to this program. It's designed for eligible veterans,
                        active-duty service members, and surviving spouses evaluating one of the most
                        competitive mortgage products available.
                    </p>
                    <h3>How it works</h3>
                    <ul>
                        <li><strong>No down payment required:</strong> eligible borrowers with full entitlement can finance 100% of the purchase price.</li>
                        <li><strong>VA funding fee:</strong> ranges from 1.25% to 3.3% depending on service history and down payment — replaces PMI and can be financed into the loan.</li>
                        <li><strong>No PMI:</strong> eliminating private mortgage insurance saves $100–$300 per month compared to a conventional loan with less than 20% down.</li>
                        <li><strong>No loan limit:</strong> borrowers with full entitlement have no VA loan limit, though lenders may apply their own guidelines.</li>
                        <li><strong>Monthly payment:</strong> principal and interest on the full financed amount (including any rolled-in funding fee) plus taxes and insurance.</li>
                    </ul>
                    <h3>Tips</h3>
                    <ul>
                        <li>A service-connected disability rating of 10% or higher may exempt you from the funding fee entirely — verify before closing.</li>
                        <li>Compare rates from multiple VA-approved lenders; VA backing doesn't standardize rates across lenders.</li>
                        <li>If you already have a VA loan, the VA IRRRL (streamline refinance) is one of the simplest and lowest-cost refinance options available.</li>
                    </ul>
                </>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                    <Field label="Home price" prefix="$" value={homePrice} onChange={setHomePrice} dataTestId="calc-input-price" />
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Down payment" suffix="%" value={downPct} onChange={setDownPct} step={0.5} dataTestId="calc-input-down" hint="VA allows 0%" />
                        <Field label="Interest rate" suffix="%" value={rate} onChange={setRate} step={0.125} dataTestId="calc-input-rate" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Loan term (years)" value={years} onChange={setYears} dataTestId="calc-input-years" />
                        <Field label="VA funding fee" suffix="%" value={fundingFeePct} onChange={setFundingFeePct} step={0.05} dataTestId="calc-input-fee" hint="2.15% first use · 0% disabled vet" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Property tax" suffix="%" value={propertyTax} onChange={setPropertyTax} step={0.05} dataTestId="calc-input-tax" />
                        <Field label="Annual home insurance" prefix="$" value={insurance} onChange={setInsurance} dataTestId="calc-input-ins" />
                    </div>
                    <label className="flex items-center gap-3 text-sm text-primary cursor-pointer">
                        <input
                            type="checkbox"
                            checked={financeFee}
                            onChange={(e) => setFinanceFee(e.target.checked)}
                            className="w-4 h-4 accent-accent"
                            data-testid="calc-input-finance-fee"
                        />
                        Finance the funding fee into the loan
                    </label>
                    <div className="pt-3 flex flex-wrap gap-4 text-xs text-muted-foreground font-mono tabular">
                        <span>Down: {fmtUSD(result.down)}</span>
                        <span>Fee: {fmtUSD(result.fee)}</span>
                        <span>Loan: {fmtUSD(result.loan)}</span>
                    </div>
                </div>
                <div className="lg:col-span-2">
                    <ResultStat
                        label="Monthly payment"
                        value={result.monthly}
                        format={(n) => fmtUSD(n, { decimals: 0 })}
                        secondary={[
                            { label: "P&I", value: result.pi, format: (n) => fmtUSD(n), testId: "calc-result-pi" },
                            { label: "Tax + Ins", value: result.tax + result.ins, format: (n) => fmtUSD(n), testId: "calc-result-escrow" },
                            { label: "Funding fee", value: result.fee, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-fee" },
                            { label: "Total paid", value: result.totalPaid, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-totalpaid" },
                        ]}
                    />
                </div>
            </div>
        </CalculatorShell>
    );
}
